/**
 * Inset-grouped directory list — one surface, hairline dividers (iOS / Slack).
 */

import type { ReactNode } from 'react';

export interface DirectoryPickerGroupProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
  readonly 'aria-busy'?: boolean;
  readonly 'aria-live'?: 'polite' | 'assertive' | 'off';
  /** Optional density marker for DirectoryResults Seznam shell. */
  readonly 'data-density'?: 'list' | 'cards';
}

export function DirectoryPickerGroup(props: DirectoryPickerGroupProps): JSX.Element {
  const { children, className, testId = 'directory-picker-group' } = props;
  const ariaBusy = props['aria-busy'];
  const ariaLive = props['aria-live'];
  const dataDensity = props['data-density'];

  return (
    <ul
      className={[
        /*
          shrink-0: overflow-hidden (for rounded corners) would otherwise let a
          flex column crush this list below content height when a tall sibling
          (e.g. guest favorites sign-in banner) shares leftover #main height.
        */
        'w-full shrink-0 overflow-hidden rounded-sm',
        /* Zero outer chrome — no border / shadow / ring / outline (mark-line ban). */
        'border-0 shadow-none outline-none ring-0',
        'bg-[var(--color-surface-elevated)]',
        'divide-y divide-[var(--color-border)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
      {...(ariaBusy === true ? { 'aria-busy': true } : {})}
      {...(ariaLive !== undefined ? { 'aria-live': ariaLive } : {})}
      {...(dataDensity !== undefined ? { 'data-density': dataDensity } : {})}
    >
      {children}
    </ul>
  );
}
